import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, PhoneOff, RefreshCw } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from './config/api';
import { formatMoney } from '../utils/formatMoney';
import { cleanCallerPhone } from '../utils/deliveryCallIntent';
import { LoadFailed } from './settings/instantSettings';
import {
  Column, DataTable, DateRange, DateRangeValue, DetailPanel, EmptyState, GHOST_BTN, KeyValue, KeyValueItem, ListPage,
  ListToolbar, PRIMARY_BTN, Pagination, StatusChip,
} from './shared/listPage';

/**
 * «Κλήσεις» (Επισκόπηση) — η σελίδα-αναφορά του «Πίνακα» (14/10/2026): ListPage + ListToolbar (αναζήτηση
 * αριθμού/πελάτη, περίοδος) + DataTable + DetailPanel με τα στοιχεία της κλήσης και τη «Μετάβαση».
 * Ίδιο endpoint με πριν: GET /api/yuboto/stores/:id/call-history?page&limit=50[&search][&date_from][&date_to],
 * ίδια «Μετάβαση» (/delivery?phone=…, πλήρης φόρτωση). Μέσα στο παράθυρο του ταμείου (embedded) χωρίς
 * κεφαλίδα: τον τίτλο τον έχει το παράθυρο.
 *
 * Χωρίς φίλτρο «Απαντημένες/Αναπάντητες» (απόφαση 12/10/2026): το modem — μόνος πάροχος σε χρήση — δεν
 * στέλνει τέλος κλήσης, η κατάσταση μένει κενή και τα φίλτρα έβγαιναν πάντα άδεια. Στήλη κατάστασης
 * μόνο όταν κάποια κλήση έχει κατάσταση (PBX), και κανένα όνομα παρόχου πουθενά.
 */

interface CallEvent {
  id: number;
  caller_phone: string;
  called_number: string | null;
  event_type: string;
  store_id: number;
  customer_id: number | null;
  received_at: string;
  call_uuid: string | null;
  call_status: 'answered' | 'missed' | null;
  customer_name: string | null;
  customer_phone: string | null;
  customer_total_orders: number | null;
  customer_total_spent: number | string | null;
}

interface PageInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const PAGE_SIZE = 50;
const EMPTY_PAGE: PageInfo = { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0 };
const YMD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Το query του endpoint. Το `date_to` πάει ως ΤΕΛΟΣ της μέρας: ο server συγκρίνει received_at <= date_to,
 * και μια σκέτη ημερομηνία είναι τα μεσάνυχτα στην ΑΡΧΗ της — ως 14/10/2026 το «Έως 14/10» έκοβε όλη την
 * 14/10, και τα έτοιμα διαστήματα («Σήμερα», «Χθες») θα έβγαιναν πάντα άδεια. Το date_from μένει ίδιο.
 */
export function callHistoryQuery(page: number, search: string, range: DateRangeValue): string {
  const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
  if (search) params.set('search', search);
  if (range.from) params.set('date_from', range.from);
  if (range.to) params.set('date_to', YMD.test(range.to) ? `${range.to}T23:59:59.999` : range.to);
  return params.toString();
}

type LoadState = 'ready' | 'error' | 'forbidden';

const formatPhone = (phone: string | null | undefined): string => {
  if (!phone) return '—';
  const clean = phone.replace(/^\+30/, '').replace(/^\+/, '');
  if (clean.length === 10) return `${clean.slice(0, 3)} ${clean.slice(3, 6)} ${clean.slice(6)}`;
  return clean;
};

// Ώρα Ελλάδας και 24ωρη, όπως η Αρχική και τα Ζ/Χ (ως 14/10 ο browser έγραφε «10:15:58 μ.μ.»).
const ATHENS = 'Europe/Athens';

const formatDate = (dateStr: string): string => new Date(dateStr).toLocaleDateString('el-GR', {
  timeZone: ATHENS, day: '2-digit', month: '2-digit', year: 'numeric',
});

const formatTime = (dateStr: string, seconds = true): string => new Date(dateStr).toLocaleTimeString('el-GR', {
  timeZone: ATHENS, hour: '2-digit', minute: '2-digit', hourCycle: 'h23', ...(seconds ? { second: '2-digit' } : {}),
});

const spentOf = (call: CallEvent): number | null => {
  if (call.customer_total_spent === null || call.customer_total_spent === undefined) return null;
  const n = parseFloat(String(call.customer_total_spent));
  return Number.isFinite(n) ? n : null;
};

// Ίδια «Μετάβαση» με πριν: πλήρης φόρτωση της οθόνης delivery με το τηλέφωνο (ίδιος καθαρισμός).
const goToDelivery = (phone: string) => {
  window.location.href = `/delivery?phone=${encodeURIComponent(cleanCallerPhone(phone))}`;
};

const CallHistory: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useTranslation(['calls', 'delivery', 'common', 'settings', 'listPage']);
  const { store } = useAuth();
  const storeId = store?.id;
  const [calls, setCalls] = useState<CallEvent[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo>(EMPTY_PAGE);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<LoadState>('ready');
  const [search, setSearch] = useState('');
  const [range, setRange] = useState<DateRangeValue>({ from: '', to: '' });
  const [selected, setSelected] = useState<CallEvent | null>(null);
  // Η αναζήτηση στέλνει αίτημα σε κάθε πλήκτρο (όπως πάντα): μετρά μόνο η απάντηση του τελευταίου.
  const seq = useRef(0);

  const { from, to } = range;
  const fetchCalls = useCallback(async (page = 1) => {
    if (!storeId) return;
    const id = ++seq.current;
    setLoading(true);
    try {
      const token = localStorage.getItem('xentrix_token');
      const res = await fetch(`${API_BASE_URL}/api/yuboto/stores/${storeId}/call-history?${callHistoryQuery(page, search, { from, to })}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (id !== seq.current) return;
      if (res.ok) {
        const data = await res.json();
        if (id !== seq.current) return;
        setCalls(data.calls || []);
        setPageInfo(data.pagination || EMPTY_PAGE);
        setState('ready');
      } else {
        setState(res.status === 403 ? 'forbidden' : 'error');
      }
    } catch (err) {
      console.error('Failed to fetch call history:', err);
      if (id === seq.current) setState('error');
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, [storeId, search, from, to]);

  useEffect(() => {
    fetchCalls(1);
  }, [fetchCalls]);

  const timeAgo = useCallback((dateStr: string): string => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('common:justNow');
    if (mins < 60) return t('common:minutesAgo', { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('common:hoursAgo', { count: hours });
    const days = Math.floor(hours / 24);
    return t('common:daysAgo', { count: days });
  }, [t]);

  const filtersActive = !!(search || range.from || range.to);
  const clearFilters = () => {
    setSearch('');
    setRange({ from: '', to: '' });
  };

  // Στήλη κατάστασης μόνο όταν κάποια κλήση έχει κατάσταση (PBX)· με το modem θα ήταν παντού «—».
  const showStatus = calls.some(c => c.call_status);

  const statusChip = useCallback((call: CallEvent) => {
    if (call.call_status === 'missed') return <StatusChip tone="danger">{t('delivery:missedCall')}</StatusChip>;
    if (call.call_status === 'answered') return <StatusChip tone="ok">{t('delivery:answeredCall')}</StatusChip>;
    return <StatusChip tone="muted">—</StatusChip>;
  }, [t]);

  const columns = useMemo<Column<CallEvent>[]>(() => [
    {
      key: 'received_at',
      header: t('common:date'),
      width: 'minmax(176px, 1.2fr)',
      render: c => (
        <>
          <span className="block tabular-nums">{formatDate(c.received_at)} · {formatTime(c.received_at, false)}</span>
          <span className="block text-xs text-t3">{timeAgo(c.received_at)}</span>
        </>
      ),
      stackRender: c => `${formatDate(c.received_at)} ${formatTime(c.received_at, false)}`,
    },
    {
      key: 'caller_phone',
      header: t('common:phone'),
      width: 'minmax(136px, 1fr)',
      stack: 'title',
      render: c => <span className="font-medium tabular-nums">{formatPhone(c.caller_phone)}</span>,
      stackRender: c => formatPhone(c.caller_phone),
    },
    {
      key: 'customer_name',
      header: t('common:customer'),
      width: 'minmax(150px, 1.4fr)',
      render: c => (c.customer_name
        ? <span>{c.customer_name}</span>
        : <span className="text-t3">{t('delivery:unknownCustomer')}</span>),
      stackRender: c => c.customer_name || t('delivery:unknownCustomer'),
    },
    {
      key: 'customer_total_orders',
      header: t('delivery:orders'),
      width: '112px',
      align: 'right',
      hideBelow: 'medium',
      render: c => (c.customer_total_orders !== null && c.customer_total_orders !== undefined
        ? <span className="text-t2">{c.customer_total_orders}</span>
        : <span className="text-t4">—</span>),
    },
    {
      key: 'customer_total_spent',
      header: t('common:total'),
      width: '120px',
      align: 'right',
      hideBelow: 'medium',
      render: c => {
        const spent = spentOf(c);
        return spent !== null ? <span className="text-t2">{formatMoney(spent)}</span> : <span className="text-t4">—</span>;
      },
    },
    ...(showStatus ? [{
      key: 'call_status',
      header: t('common:status'),
      width: '132px',
      render: statusChip,
    } as Column<CallEvent>] : []),
    {
      key: 'go',
      header: <span className="sr-only">{t('delivery:navigate')}</span>,
      width: '140px',
      align: 'right',
      hideBelow: 'narrow',
      // Η γρήγορη «Μετάβαση» της γραμμής μένει (ένα πάτημα, όπως πριν)· το κλικ στη γραμμή ανοίγει τα στοιχεία.
      // Χωρίς περίγραμμα: σε κάθε γραμμή, ένα κουτί ανά κλήση θα φόρτωνε τον πίνακα.
      render: c => (
        <button
          type="button"
          onClick={() => goToDelivery(c.caller_phone)}
          title={t('delivery:navigateToDelivery')}
          className="inline-flex items-center gap-1.5 min-h-[36px] px-2.5 rounded-lg text-sm font-medium text-t2 hover:text-t1 hover:bg-surface-3 whitespace-nowrap transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          {t('delivery:navigate')}
        </button>
      ),
    },
  ], [t, timeAgo, showStatus, statusChip]);

  const detailItems = (call: CallEvent): KeyValueItem[] => {
    const spent = spentOf(call);
    return [
      {
        id: 'customer',
        label: t('common:customer'),
        value: call.customer_name || t('delivery:unknownCustomer'),
        hint: call.customer_name ? undefined : t('calls:detail.unknownHint'),
      },
      { id: 'caller', label: t('calls:detail.caller'), value: <span className="tabular-nums">{formatPhone(call.caller_phone)}</span> },
      ...(call.called_number ? [{ id: 'line', label: t('calls:detail.line'), value: formatPhone(call.called_number) }] : []),
      {
        id: 'received',
        label: t('calls:detail.received'),
        value: `${formatDate(call.received_at)} · ${formatTime(call.received_at)}`,
        hint: timeAgo(call.received_at),
      },
      ...(call.call_status ? [{ id: 'status', label: t('common:status'), value: statusChip(call) }] : []),
      { id: 'orders', label: t('delivery:orders'), value: call.customer_total_orders ?? null },
      { id: 'spent', label: t('calls:detail.spent'), value: spent !== null ? formatMoney(spent) : null },
    ];
  };

  const firstLoad = loading && calls.length === 0;

  return (
    <ListPage
      embedded={embedded}
      eyebrow={t('settings:nav.groups.overview')}
      title={t('settings:nav.callHistory')}
      subtitle={t('calls:subtitle')}
      testId="call-history"
    >
      <ListToolbar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: t('delivery:searchPhoneOrName'),
          ariaLabel: t('calls:searchLabel'),
        }}
        filters={(
          <>
            <DateRange from={range.from} to={range.to} onChange={setRange} />
            {filtersActive && (
              <button type="button" className={GHOST_BTN} onClick={clearFilters}>{t('common:clear')}</button>
            )}
          </>
        )}
        actions={(
          <button type="button" className={GHOST_BTN} onClick={() => fetchCalls(pageInfo.page)} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {t('common:refresh')}
          </button>
        )}
        summary={state === 'ready' && !firstLoad ? t('calls:count', { count: pageInfo.total }) : null}
      />

      {state !== 'ready' ? (
        <LoadFailed message={state === 'forbidden' ? t('calls:forbidden') : t('calls:loadFailed')} onRetry={() => fetchCalls(pageInfo.page)} />
      ) : (
        <DataTable
          columns={columns}
          rows={calls}
          rowKey={c => c.id}
          onRowClick={setSelected}
          selectedKey={selected?.id ?? null}
          loading={loading}
          stickyHeader={!embedded}
          rowLabel={c => t('calls:rowLabel', { phone: formatPhone(c.caller_phone), when: `${formatDate(c.received_at)} ${formatTime(c.received_at, false)}` })}
          empty={(
            <EmptyState
              icon={PhoneOff}
              title={t('delivery:noCallsFound')}
              hint={filtersActive ? t('delivery:tryDifferentCriteria') : t('delivery:incomingCallsWillAppear')}
            />
          )}
          footer={(
            <Pagination
              page={pageInfo.page}
              pageSize={pageInfo.limit || PAGE_SIZE}
              total={pageInfo.total}
              onPage={p => fetchCalls(p)}
              disabled={loading}
            />
          )}
        />
      )}

      <DetailPanel
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? formatPhone(selected.caller_phone) : ''}
        subtitle={selected ? `${formatDate(selected.received_at)} · ${formatTime(selected.received_at, false)} · ${timeAgo(selected.received_at)}` : undefined}
        footer={selected && (
          <>
            <button type="button" className={GHOST_BTN} onClick={() => setSelected(null)}>{t('listPage:close')}</button>
            <button type="button" className={PRIMARY_BTN} onClick={() => goToDelivery(selected.caller_phone)}>
              <ExternalLink className="w-4 h-4" aria-hidden="true" />
              {t('calls:detail.goToDelivery')}
            </button>
          </>
        )}
        testId="call-detail"
      >
        {selected && <KeyValue items={detailItems(selected)} testId="call-detail-values" />}
      </DetailPanel>
    </ListPage>
  );
};

export default CallHistory;
